<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-dumper-overview">
    <nx-report-header title="翻车实时总览">
      <div class="kb-header-actions">
        <span class="kb-toolbar-hint">业务日</span>
        <el-date-picker
          v-model="queryDate"
          type="date"
          value-format="yyyy-MM-dd"
          placeholder="业务日"
          size="small"
          style="width: 150px"
          @change="loadData"
        />
        <span class="kb-toolbar-hint">班次</span>
        <el-select v-model="shift" size="small" style="width: 200px" @change="loadData">
          <el-option label="自动（当前班次）" value="" />
          <el-option label="早班 06:00–14:00" value="early" />
          <el-option label="中班 14:00–22:00" value="middle" />
          <el-option label="夜班 22:00–06:00" value="night" />
          <el-option label="全日汇总" value="all" />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <div class="kb-kpi-row cols-6">
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">本班计划翻卸</div>
        <div class="kb-kpi-value">
          {{ data.shiftPlannedCarriages }}<span class="kb-unit">节</span>
        </div>
        <div class="kb-kpi-sub">{{ data.shiftName }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">本班已翻</div>
        <div class="kb-kpi-value is-ok">
          {{ data.shiftTippedCarriages }}<span class="kb-unit">节</span>
        </div>
        <div class="kb-kpi-sub">完成率 {{ shiftRateText }}</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">本班完成率</div>
        <div class="kb-kpi-value">{{ shiftRateText }}</div>
        <div class="kb-kpi-sub">计划为 0 时显示 0%</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">当前在翻</div>
        <div class="kb-kpi-value is-warn">{{ currentWorkingText }}</div>
        <div class="kb-kpi-sub">作业中车厢数</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">实时效率</div>
        <div class="kb-kpi-value is-ok">{{ effText }}</div>
        <div class="kb-kpi-sub">节/小时</div>
      </div>
      <div class="kb-kpi-card">
        <div class="kb-kpi-label">设备 OPC</div>
        <div class="kb-kpi-value is-ok">{{ opcRunLabel }}</div>
      </div>
    </div>

    <div class="do-strip">
      <span v-if="data.opcRunning" class="do-pill run"><span class="do-pulse" aria-hidden="true" />OPC 运行信号</span>
      <span v-else class="do-pill stop">OPC 停止/未知</span>
      <span class="do-pill" :class="scenePillClass">{{ sceneLabel }}</span>
    </div>

    <div class="kb-two-col">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>当日翻卸节数累计</span>
        </div>
        <div ref="chartCum" class="kb-chart-box" />
      </div>
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>本班完成率</span>
        </div>
        <div ref="chartGauge" class="kb-chart-box kb-chart-box--sm" />
        <div v-if="planBrief" class="kb-train-box">
          <strong>当前计划</strong> {{ planBrief }}<br />
          <strong>在翻车厢</strong> {{ workingNos }}<br />
          <strong>待翻</strong> {{ pendingCnt }} 节
        </div>
      </div>
    </div>

    <div class="kb-two-col">
      <div class="kb-chart-card">
        <div class="kb-chart-head">
          <span>小时翻卸量</span>
        </div>
        <div ref="chartDelta" class="kb-chart-box" />
      </div>
      <div class="kb-section">
        <div class="kb-section-head">
          <span>待作业队列</span>
        </div>
        <el-table :data="data.queue" border stripe size="small" style="width: 100%">
          <el-table-column prop="trainNumber" label="车次" min-width="120" />
          <el-table-column prop="planNo" label="计划号" min-width="120" />
          <el-table-column prop="railPlanPhaseText" label="阶段" width="130" />
          <el-table-column prop="plannedCarriages" label="计划节" width="80" align="right" />
          <el-table-column prop="tippedCarriageCount" label="已翻" width="70" align="right" />
        </el-table>
      </div>
    </div>

    <div class="kb-section">
      <div class="kb-section-head">
        <span>最近完成车厢</span>
      </div>
      <el-table :data="data.recentCarriages" border stripe size="small" style="width: 100%">
        <el-table-column prop="trainNumber" label="车次" width="140" />
        <el-table-column prop="carriageNumber" label="车厢号" width="140" />
        <el-table-column prop="updateTime" label="更新时间" min-width="160">
          <template slot-scope="{ row }">{{ fmtTs(row.updateTime) }}</template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchDumperRealtimeOverview } from './api'

export default {
  name: 'KanbanDumperRealtimeOverview',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      queryDate: null,
      shift: '',
      data: {
        shiftPlannedCarriages: 0,
        shiftTippedCarriages: 0,
        shiftCompletionRatePercent: null,
        shiftName: '',
        opcRunning: false,
        opcPointFound: false,
        opcTag: '',
        opcCurrentValue: '',
        currentWorkingCarriages: null,
        realtimeEfficiencyCarsPerHour: null,
        hourLabels: [],
        hourlyTippedDelta: [],
        hourlyTippedCumulative: [],
        queue: [],
        recentCarriages: [],
        currentPlan: { plan: {}, scene: '' }
      },
      chartCum: null,
      chartDelta: null,
      chartGauge: null
    }
  },
  computed: {
    shiftRateText() {
      const p = this.data.shiftCompletionRatePercent
      if (p == null || p === '') return '0%'
      const n = typeof p === 'number' ? p : parseFloat(String(p))
      if (Number.isNaN(n)) return '0%'
      return `${n.toFixed(1)}%`
    },
    currentWorkingText() {
      const n = this.data.currentWorkingCarriages
      return n != null && n !== '' ? `${n} 节` : '—'
    },
    effText() {
      const e = this.data.realtimeEfficiencyCarsPerHour
      if (e == null || e === '') return '—'
      return typeof e === 'number' ? e.toFixed(2) : String(e)
    },
    opcRunLabel() {
      if (!this.data.opcPointFound) return '无点位'
      return this.data.opcRunning ? '运行' : '停止'
    },
    sceneLabel() {
      const s = this.data.currentPlan && this.data.currentPlan.scene
      const map = {
        WORKING: '场景 · 正在作业',
        PENDING: '场景 · 待作业/待确认',
        LAST_COMPLETED: '场景 · 最近完成',
        NONE: '场景 · 无计划'
      }
      return map[s] || (s ? `场景 · ${s}` : '场景 · —')
    },
    scenePillClass() {
      const s = this.data.currentPlan && this.data.currentPlan.scene
      return s === 'WORKING' ? 'run' : 'stop'
    },
    planBrief() {
      const pl = this.data.currentPlan && this.data.currentPlan.plan
      if (!pl) return ''
      const tn = pl.trainNumber || '—'
      const done = pl.tippedCarriageCount != null ? pl.tippedCarriageCount : 0
      const tot = pl.plannedCarriages != null ? pl.plannedCarriages : 0
      return `${tn} · 已翻 ${done} / ${tot} 节`
    },
    workingNos() {
      const pl = this.data.currentPlan && this.data.currentPlan.plan
      if (!pl || !pl.workingCarriageNumbers || !pl.workingCarriageNumbers.length) return '—'
      return pl.workingCarriageNumbers.join('、')
    },
    pendingCnt() {
      const pl = this.data.currentPlan && this.data.currentPlan.plan
      if (!pl || pl.pendingCarriageCount == null) return '—'
      return pl.pendingCarriageCount
    }
  },
  mounted() {
    this.queryDate = this.formatDateStr(new Date())
    this.loadData()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.disposeCharts()
  },
  methods: {
    formatDateStr(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    fmtTs(v) {
      if (!v) return '—'
      const d = new Date(v)
      if (Number.isNaN(d.getTime())) return String(v)
      const p = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
    },
    async loadData() {
      this.loading = true
      try {
        const params = {}
        if (this.queryDate) params.queryDate = this.queryDate
        if (this.shift) params.shift = this.shift
        const raw = await fetchDumperRealtimeOverview(params)
        this.data = Object.assign({}, this.data, raw || {})
        this.$nextTick(() => this.renderCharts())
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    disposeCharts() {
      [this.chartCum, this.chartDelta, this.chartGauge].forEach((c) => {
        if (c) c.dispose()
      })
      this.chartCum = this.chartDelta = this.chartGauge = null
    },
    onResize() {
      [this.chartCum, this.chartDelta, this.chartGauge].forEach((c) => c && c.resize())
    },
    renderCharts() {
      if (!this.$refs.chartCum) return
      this.disposeCharts()
      const labels = this.data.hourLabels || []
      const cum = this.data.hourlyTippedCumulative || []
      const delta = this.data.hourlyTippedDelta || []
      const rate = Number(this.data.shiftCompletionRatePercent) || 0

      const c1 = echarts.init(this.$refs.chartCum)
      this.chartCum = c1
      c1.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 48, right: 24, top: 28, bottom: 28 },
        xAxis: { type: 'category', data: labels, axisLabel: { color: '#64748b', rotate: labels.length > 14 ? 30 : 0 } },
        yAxis: { type: 'value', name: '节', splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } } },
        series: [{
          type: 'line',
          smooth: true,
          data: cum,
          lineStyle: { width: 3, color: '#0284c7' },
          areaStyle: { opacity: 0.12, color: '#0284c7' }
        }]
      })

      const c2 = echarts.init(this.$refs.chartDelta)
      this.chartDelta = c2
      c2.setOption({
        tooltip: { trigger: 'axis' },
        grid: { left: 44, right: 20, top: 24, bottom: 28 },
        xAxis: { type: 'category', data: labels, axisLabel: { color: '#64748b', fontSize: 10, rotate: labels.length > 14 ? 30 : 0 } },
        yAxis: { type: 'value', name: '节', splitLine: { lineStyle: { type: 'dashed', color: '#e2e8f0' } } },
        series: [{
          type: 'bar',
          data: delta,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: '#38bdf8' },
              { offset: 1, color: '#0284c7' }
            ]),
            borderRadius: [4, 4, 0, 0]
          }
        }]
      })

      const g = echarts.init(this.$refs.chartGauge)
      this.chartGauge = g
      g.setOption({
        series: [{
          type: 'gauge',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          progress: { show: true, width: 12, itemStyle: { color: '#0284c7' } },
          axisLine: { lineStyle: { width: 12, color: [[1, '#e2e8f0']] } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          detail: { fontSize: 22, fontWeight: 700, color: '#0369a1', formatter: '{value}%' },
          data: [{ value: Math.min(100, Math.round(rate * 10) / 10), name: '本班' }]
        }]
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
